import React, { useMemo, useState } from "react";
import {
  Card,
  CardContent,
  Box,
  Typography,
  Select,
  MenuItem,
  SelectChangeEvent, // ✅ import this
} from "@mui/material";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  ReferenceLine,
} from "recharts";
import { useDispatch } from "react-redux";
import { ActiveUserFilter } from "../../Redux/Slices/GateAdminDashboard/ActiveUserFilter";

interface ActiveUsersChartProps {
  data: { day: string; count: number }[];
}

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    return (
      <Box
        sx={{
          backgroundColor: "#fff",
          border: "1px solid #E0E0E0",
          borderRadius: "8px",
          px: 1.5,
          py: 1,
          boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
        }}
      >
        <Typography
          variant="body2"
          sx={{ fontWeight: 500, color: "#424242", fontSize: 12 }}
        >
          Student Count: {payload[0].value}
        </Typography>
      </Box>
    );
  }
  return null;
};

const ActiveUsersChart: React.FC<ActiveUsersChartProps> = ({ data }) => {
  const dispatch = useDispatch();
  const [filterValue, setFilterValue] = useState("MONTHLY");
  const currentDay = "Sun";

  const { yDomain, yTicks } = useMemo(() => {
    const safeData = Array.isArray(data) ? data : [];

    const maxValue =
      safeData.length > 0 ? Math.max(...safeData.map((d) => d.count)) : 0;
    if (maxValue <= 100) {
      return { yDomain: [0, 100], yTicks: [0, 10, 30, 70, 100] };
    } else if (maxValue <= 200) {
      return { yDomain: [0, 200], yTicks: [0, 50, 100, 150, 200] };
    } else {
      return { yDomain: [0, 500], yTicks: [0, 100, 200, 300, 400, 500] };
    }
  }, [data]);

  const handleFilterChange = (event: SelectChangeEvent<string>) => {
    const newValue = event.target.value;
    setFilterValue(newValue);
    dispatch(ActiveUserFilter({ FilterBy: newValue }));
  };

  return (
    <Card
      sx={{
        borderRadius: 3,
        boxShadow: "0px 2px 10px rgba(0,0,0,0.08)",
        overflow: "hidden",
      }}
    >
      <CardContent sx={{ p: 2.5, pb: 1 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography
            variant="subtitle1"
            fontWeight={600}
            sx={{ color: "#212121" }}
          >
            Active Users
          </Typography>

          <Select
            size="small"
            value={filterValue}
            onChange={handleFilterChange}
            sx={{
              fontSize: 12,
              height: 28,
              backgroundColor: "#017CC9",
              px: 1.5,
              py: 0.4,
              borderRadius: 1,
              cursor: "pointer",
              fontWeight: 500,
              color: "#ffffff",
              "& .MuiSelect-select": { py: 0.5 },
              "& .MuiSvgIcon-root": {
                color: "#fff", 
              },
            }}
          >
            <MenuItem value="MONTHLY">Monthly</MenuItem>
            <MenuItem value="WEEKLY">Weekly</MenuItem>
            <MenuItem value="YEARLY">Yearly</MenuItem>
          </Select>
        </Box>

        {/* Chart (unchanged) */}
        <Box sx={{ height: 250 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={data}
              margin={{ top: 10, right: 10, left: 10, bottom: 20 }}
            >
              <defs>
                <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#42A5F5" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#42A5F5" stopOpacity={0.1} />
                </linearGradient>
              </defs>

              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#E5E7EB"
                vertical={false}
              />
              <XAxis
                dataKey="day"
                tick={{ fontSize: 12, fill: "#616161", fontWeight: 500 }}
                axisLine={{ stroke: "#E0E0E0", strokeWidth: 1 }}
                tickLine={false}
                dy={8}
              />
              <YAxis
                tick={{ fontSize: 12, fill: "#616161", fontWeight: 500 }}
                axisLine={{ stroke: "#E0E0E0", strokeWidth: 1 }}
                tickLine={false}
                width={30}
                domain={yDomain}
                ticks={yTicks}
              />
              <Tooltip
                content={<CustomTooltip />}
                cursor={{
                  stroke: "#1976d2",
                  strokeWidth: 2,
                  strokeDasharray: "5 5",
                }}
              />
              <ReferenceLine
                x={currentDay}
                stroke="#1976d2"
                strokeDasharray="3 3"
                strokeWidth={1.5}
              />
              <Area
                type="monotone"
                dataKey="count"
                stroke="#1976d2"
                strokeWidth={2.5}
                fill="url(#colorCount)"
                dot={false}
                activeDot={{
                  r: 5,
                  fill: "#1976d2",
                  stroke: "#fff",
                  strokeWidth: 2,
                }}
                animationDuration={1200}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Box>

        {/* Legend */}
        {/* <Box
          sx={{
            mt: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              bgcolor: "#1976d2",
            }}
          />
          <Typography sx={{ fontSize: 12, color: "#616161", fontWeight: 500 }}>
            Student Count
          </Typography>
        </Box> */}
      </CardContent>
    </Card>
  );
};

export default ActiveUsersChart;
